import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  CoordinateBounds,
  useCoordinatesFromGPX,
} from "../hooks/use-coordinates";
import {
  PosterProject,
  defaultCoordinateLimit,
  PosterSizePreset,
  PosterLegend,
  StyledPoint,
  MapLabel,
  MapLabelType,
  MapLabelStyle,
  StaticLabel,
  Backdrop,
  BackdropType,
  BackdropStyle,
  GPSSource,
  PhotoWithCoordinates,
} from "./poster-project";
import { migrateProject } from "./project-migrator";
import { ProjectTemplate } from "./project-template";
import { defaultGPSSource, defaultProject } from "./default-project-style";
import { applyLabelStyleToAllPointsInProject } from "BEDROCK/utility/apply-to-all";

import { Point } from "./poster-types";
import { Coordinate } from "../hooks/use-coordinates";
import { MapStaticImageOptions, MapStyleBaseURL } from "./poster-project";
import { useMapBoxUtils } from "PosterEditor/hooks/use-mapbox-utils";
import { LineStyle, LinePattern } from "./poster-types";
import { PointStyle } from "./poster-types";
import { Thumbnail as MapStyleThumbnail } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-thumbnail-grid";
import { generateMapStyles } from "./poster-project";
import { v4 as uuid } from "uuid";
import { useGpsStore } from "entity-store/gps-store/use-gps-store";
import { useAccount } from "BEDROCK/account/use-account";
import {
  EditingPermissions,
  PointEditingPermissions,
} from "./editing-permissions";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { useImageElementCache } from "./use-image-element-cache";
import { Shadow } from "./poster-types";
import { GPSData } from "./poster-project";
import {
  useSizePresets,
  SizePreset,
  Orientation,
} from "./size-presets/size-preset";
import { Size } from "./poster-types";

interface PosterProjectContextType {
  project: PosterProject;

  setLastRenderID: (id: string) => void;
  markRendered: (rendered: boolean) => void;

  template: ProjectTemplate | undefined;
  setTemplate: (template: ProjectTemplate) => void;
  setTemplateThumbnail: (thumbnail: string) => void;

  isTemplateEditing: boolean;
  setEditingPermissions: (permissions: EditingPermissions) => void;

  // Turns a draft into a real project by setting `isTemplateDraft` to false.
  saveDraft: () => void;
  existingEditedDraftID: () => string | undefined;
  deleteDraft: () => Promise<void>;

  resetProject: () => void;

  selectionColor: string;

  mapStyleID: string;
  mapStyles: MapStyleThumbnail[];

  loadSignedURLS: () => Promise<void>;
  cacheImageForPointStyle: (pointStyle: PointStyle) => Promise<void>;
  cacheImageForMapLabelStyle: (labelStyle: MapLabelStyle) => Promise<void>;
  cacheImageForBackdropStyle: (backdropStyle: BackdropStyle) => Promise<void>;

  setCoordinateBounds: (bounds: CoordinateBounds | undefined) => void;

  setMapStyle: (styleID: string) => void;

  setMainRouteColor: (color: string) => void;
  setFlankRouteColor: (color: string) => void;

  setLineWidth: (width: number) => void;
  setLinePattern: (linePattern: LinePattern) => void;
  setLineDotDash: (dotDash: boolean) => void;
  setLineShadow: (shadow: Shadow | undefined) => void;

  setCoordinates: (
    coordinates: Coordinate[],
    sourceFileName: string,
    data: GPSData | undefined
  ) => void;
  setCoordinateLimit: (limit: number) => void;
  deleteCoordinate: (coordinate: Coordinate) => void;
  deleteAllCoordinates: () => void;
  gpsSource: GPSSource | undefined;

  styledPointAtCoordinate: (
    point: Coordinate
  ) => { point: StyledPoint; index: number } | undefined;
  createStyledPointFromPhoto: (
    photo: PhotoWithCoordinates
  ) => Promise<StyledPoint>;
  styledPointExistsForPhoto: (url: string) => boolean;
  removeStyledPointForPhoto: (url: string) => void;

  setStyledPoint: (point: StyledPoint) => void;
  setPointStyle: (
    point: Coordinate,
    style: PointStyle | undefined,
    existingID: string | undefined,
    label: MapLabel | undefined
  ) => void;
  addNewStyledPoint: (coordinate: Coordinate) => StyledPoint;
  removeAllStyledPoints: () => void;
  setStyledPointEditingPermissions: (
    point: StyledPoint,
    permissions: PointEditingPermissions
  ) => void;
  styledPointWithID: (id: string) => StyledPoint | undefined;

  // getMapLabel: (id: string) => MapLabel | undefined;
  setMapLabel: (label: MapLabel) => void;
  addNewMapLabel: (
    coordinate: Coordinate,
    type: MapLabelType,
    pointID: string
  ) => MapLabel;
  // removeMapLabel: (label: MapLabel) => void;
  styledPointsRequiringBoundsUpdate: StyledPoint[];
  clearStyledPointsRequiringBoundsUpdate: () => void;

  getStaticLabel: (id: string) => StaticLabel | undefined;
  setStaticLabel: (label: StaticLabel) => void;
  addNewStaticLabel: (point: Point, relativeFontSize: number) => StaticLabel;
  removeStaticLabel: (label: StaticLabel) => void;

  getBackdrop: (id: string) => Backdrop | undefined;
  setBackdrop: (backdrop: Backdrop) => void;
  addNewBackdrop: (point: Point, type: BackdropType) => Backdrop;
  removeBackdrop: (backdrop: Backdrop) => void;
  setBackdrops: (backdrops: Backdrop[]) => void;

  posterSizePresets: SizePreset[];
  setSizePresetID: (id: string, customSizePX?: Size | undefined) => void;
  setOrientation: (orientation: Orientation) => void;

  setMapSize: (size: PosterSizePreset) => void;

  setBorderWidthMM: (width: number) => void;
  setBorderColor: (color: string) => void;

  applyBorderAndPointerStyleToAllPoints: (style: MapLabelStyle) => void;

  staticMapImageURL: (options: MapStaticImageOptions) => string | null;
  staticQuadrantImageURL: (
    options: MapStaticImageOptions
  ) => { name: string; url: string }[];

  // Expose image cache for preloaded backdrops
  imageCache: Map<string, HTMLImageElement>;
}

const PosterProjectContext = createContext<
  PosterProjectContextType | undefined
>(undefined);

interface PosterProjectProviderProps {
  children: ReactNode;
  initialProject: PosterProject | undefined;
  // If this is passed in, it'll contain a reference to the same project as above.
  // the project above will be edited, and on save-to-template, this template.project should be updated.
  projectTemplate: ProjectTemplate | undefined;
}

const PosterProjectProvider = ({
  children,
  initialProject,
  projectTemplate,
}: PosterProjectProviderProps) => {
  // Image cache for preloaded backdrops
  const {
    projects: projectsInStore,
    saveProject,
    deleteProjectAsync,
  } = useProjectStore();
  const { saveTemplate } = useTemplateStore();
  const { copyImageFromPublicToUser, duplicateUserImage } = useImageStore();
  const {
    loadCoordinates: loadCoordinatesFromStore,
    saveCoordinates: saveCoordinatesToStore,
    deleteCoordinates: deleteCoordinatesFromStore,
  } = useGpsStore();
  const { account } = useAccount();

  const { sizePresets } = useSizePresets();

  //Todo: MIGRATE ANY REFERENCE TO PROJECT ROUTESOURCE TO THIS.
  //FOR REDUCING LIIMIT ETC, EVERY MENTION.
  const [gpsSource, setGPSSource] = useState<GPSSource | undefined>(undefined);

  const [project, setProject] = useState<PosterProject>(
    migrateProject(initialProject ?? defaultProject)
  );

  const [template, setTemplate] = useState<ProjectTemplate | undefined>(
    projectTemplate
  );

  const [
    styledPointsRequiringBoundsUpdate,
    setStyledPointsRequiringBoundsUpdate,
  ] = useState<StyledPoint[]>([]);

  const [immediateGPSSourceSaveSemaphore, setImmediateGPSSourceSaveSemaphore] =
    useState<boolean>(false);

  const isTemplateEditing =
    account?.permissions === "admin" && template !== undefined;

  const {
    imageCache,
    cacheImage,
    cacheImageForBackdropStyle,
    cacheImageForMapLabelStyle,
    cacheImageForPointStyle,
  } = useImageElementCache({
    isPublic: isTemplateEditing || project.isTemplateDraft,
    projectID: project.id,
  });

  const saveToTemplate = () => {
    if (template) {
      template.project = project;
      setTemplate(template);
      saveTemplate(template, false);
    }
  };

  const saveTemplateToStore = (saveNow: boolean) => {
    if (template) {
      saveTemplate(template, saveNow);
    }
  };

  useEffect(() => {
    if (!gpsSource) {
      return;
    }
    if (project.isTemplateDraft) {
      return;
    }
    if (template) {
      //Save as public GPS source.
      saveCoordinatesToStore(
        gpsSource,
        gpsSource?.id ?? "",
        true,
        immediateGPSSourceSaveSemaphore
      );
      setImmediateGPSSourceSaveSemaphore(false);
    } else {
      //Save as private GPS source.
      saveCoordinatesToStore(
        gpsSource,
        gpsSource?.id ?? "",
        false,
        immediateGPSSourceSaveSemaphore
      );
      setImmediateGPSSourceSaveSemaphore(false);
    }
  }, [
    gpsSource,
    project.gpsSourceIDs,
    template,
    immediateGPSSourceSaveSemaphore,
  ]);

  const loadGPSSources = (project: PosterProject) => {
    const ids = project.gpsSourceIDs;
    if (!ids) {
      setGPSSource(defaultGPSSource);
      return;
    }
    const isPublic = template !== undefined || project.isTemplateDraft;

    ids.forEach((id) => {
      loadCoordinatesFromStore(id, isPublic).then((source) => {
        if (source) {
          setGPSSource(source);
        }
      });
    });
  };

  const loadSignedURLS = async () => {
    await Promise.all(
      project.backdrops.map(async (backdrop) => {
        await cacheImageForBackdropStyle(backdrop.style);
      })
    );
    await Promise.all(
      project.styledPoints.map(async (point) => {
        await cacheImageForPointStyle(point.style);
      })
    );

    const mapLabels = project.styledPoints.map((point) => point.label);
    await Promise.all(
      mapLabels.map(async (label) => {
        if (label) await cacheImageForMapLabelStyle(label.style);
      })
    );

    //Placeholder image for maplabel.
    await cacheImage("placeholder", "/placeholders/photo-placeholderpng.png");
  };

  const [mapStyleThumbnails, setMapStyleThumbnails] = useState<
    MapStyleThumbnail[]
  >([]);

  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  const { getStaticMapURL, getQuadrantURLs } = useMapBoxUtils(accessToken);

  const selectionColor = useMemo(() => "gray", []);

  const {
    coordinates: testCoordinates,
    reduceCoordinates,
    reduceCoordinatesFavouringDistance,
    loading,
    error,
  } = useCoordinatesFromGPX(defaultCoordinateLimit);

  useEffect(() => {
    if (template) {
      // The project being edited is part of a template, so update it on the template rather than to
      // template storage.
      saveToTemplate();
    } else {
      project.updatedAt = new Date();
      saveProject(project);
    }
  }, [
    //project,
    project.borderStyle,
    project.size,
    project.sizeOption,
    // project.sizeOption.presetID,
    project.mapStyle,
    project.lineStyle,
    gpsSource?.allCoordinates,
    project.styledPoints,
    project.mapLabels,
    project.styleUpdateKey,
    project.labelUpdateKey,
    project.staticLabels,
    project.backdrops,
    project.legend,
    project.bounds,
    project.gpsSourceIDs,
  ]);

  useEffect(() => {
    saveTemplateToStore(false);
  }, [template]);

  useEffect(() => {
    if (project) {
      loadGPSSources(project);
    }
  }, []);

  useEffect(() => {
    generateMapStyleThumbnailsFromManifest();
  }, []);

  useEffect(() => {
    saveTemplateToStore(false);
  }, [template?.thumbnail]);

  const saveDraft = () => {
    // there is no template for a project started from a draft but not saved yet,
    // so copy over the gpsSourceID from template to project when it's uploaded.
    const updatedProject = {
      ...project,
      isRendered: false,
      isTemplateDraft: false,
    };
    setProject(updatedProject);

    // Save immediately with the updated project
    saveProject(updatedProject, true);
    if (gpsSource) {
      saveCoordinatesToStore(gpsSource, gpsSource?.id ?? "", false, true);
    }

    copyImagesFromPublicToUser();
  };

  const existingEditedDraftID = (): string | undefined => {
    const firstProject = projectsInStore?.filter(
      (p) =>
        p.isInCheckout === false && p.isOrdered === false && p.id !== project.id
    )[0];
    return firstProject?.id;
  };

  const deleteDraft = async (): Promise<void> => {
    const projects = projectsInStore?.filter(
      (p) =>
        p.isInCheckout === false && p.isOrdered === false && p.id !== project.id
    );
    if (projects && projects?.length > 0) {
      await Promise.all(projects.map((proj) => deleteProjectAsync(proj)));
    }
  };

  const copyImagesFromPublicToUser = () => {
    project.mapLabels = project.mapLabels.map((label) => {
      if (label.type === "image" && label.style.imageName) {
        copyImageFromPublicToUser(label.style.imageName, `labels`, project.id);
      }
      return label;
    });
    project.styledPoints.map((point) => {
      if (point.style.imageName) {
        copyImageFromPublicToUser(
          point.style.imageName,
          `pointIcons`,
          project.id
        );
      }
      return point;
    });
    project.backdrops.map((backdrop) => {
      if (backdrop.style.imageName) {
        copyImageFromPublicToUser(
          backdrop.style.imageName,
          `backdrops`,
          project.id
        );
      }
      return backdrop;
    });
  };

  const resetProject = () => {
    setProject(defaultProject);
  };

  const setLastRenderID = useCallback((id: string) => {
    setProject((prevProject) => ({
      ...prevProject,
      lastRenderID: id,
    }));
  }, []);

  const markRendered = useCallback((rendered: boolean) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: rendered,
    }));
  }, []);

  const generateMapStyleThumbnailsFromManifest = async () => {
    const mapStyles = await generateMapStyles();
    const thumbnails: MapStyleThumbnail[] = [];
    for (const style of mapStyles) {
      thumbnails.push({
        id: style.mapBoxStyleID,
        src: `/pallete-components/map-thumbnails/${style.mapBoxStyleID}.png`,
        alt: `Map style ${style.name}`,
      });
    }
    setMapStyleThumbnails(thumbnails);
  };

  const reduceCoordinatesToLimit = useCallback(
    (limit: number) => {
      if (!gpsSource) {
        return;
      }
      const reduced = reduceCoordinates(gpsSource.allCoordinates, limit);
      setGPSSource((prev) => ({
        ...(prev ?? defaultGPSSource),
        displayedCoordinates: reduced,
        coordinateLimit: limit,
      }));
    },
    [gpsSource, reduceCoordinates]
  );

  // useEffect(() => {
  //   if (testCoordinates.length > 0) {
  //     setReduceCoordinates(testCoordinates);
  //   }
  // }, [testCoordinates]);

  const setReduceCoordinates = (
    coordinates: Coordinate[],
    name: string,
    gpsData: GPSData | undefined
  ): string => {
    const reduced = reduceCoordinatesFavouringDistance(
      coordinates,
      gpsSource?.coordinateLimit ?? defaultCoordinateLimit
    );

    const newID = uuid();

    const source: GPSSource = {
      id: newID,
      name: name,
      originDescription: "GPS",
      allCoordinates: coordinates,
      displayedCoordinates: reduced,
      data: gpsData,
    };

    setProject((prev) => ({
      ...prev,
      gpsSource: [],
      isRendered: false,
      gpsSourceIDs: [newID],
    }));

    setGPSSource(source);

    return newID;
  };

  useEffect(() => {
    if ((gpsSource?.allCoordinates?.length ?? 0) > 0) {
      if (!project || gpsSource?.allCoordinates.length === 0) {
        reduceCoordinatesToLimit(defaultCoordinateLimit);
      }
    }
  }, [project, reduceCoordinates, reduceCoordinatesToLimit]);

  const setCoordinateLimit = (limit: number) => {
    // We still need a coordinateLimit state because the actual number of coordinates
    // we reduce to might not exactly be what the input requests.
    const coords = reduceCoordinatesFavouringDistance(
      gpsSource?.allCoordinates ?? [],
      limit
    );
    setGPSSource((prev) => ({
      ...(prev ?? defaultGPSSource),
      displayedCoordinates: coords,
    }));
  };

  const setSizePresetID = useCallback(
    (id: string, customSizePX?: Size | undefined) => {
      setProject((prevProject) => ({
        ...prevProject,
        isRendered: false,
        sizeOption: {
          presetID: id,
          options: {
            ...prevProject.sizeOption.options,
            widthPX: customSizePX?.width ?? undefined,
            heightPX: customSizePX?.height ?? undefined,
          },
        },
      }));
    },
    []
  );

  const setOrientation = useCallback((orientation: Orientation) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      sizeOption: {
        ...prevProject.sizeOption,
        options: { ...prevProject.sizeOption.options, orientation },
      },
    }));
  }, []);

  const setMapSize = (size: PosterSizePreset) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      size,
    }));
  };

  const deleteCoordinate = (coordinate: Coordinate) => {
    if (gpsSource) {
      const newCoordinates = gpsSource.displayedCoordinates.filter(
        (coord) => coord.lat !== coordinate.lat && coord.lng !== coordinate.lng
      );

      setGPSSource((prev) => ({
        ...(prev ?? defaultGPSSource),
        displayedCoordinates: newCoordinates,
      }));
    }

    const newStyledPoints = project.styledPoints.filter(
      (point) =>
        point.coordinate.lat !== coordinate.lat &&
        point.coordinate.lng !== coordinate.lng
    );

    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints: [...newStyledPoints],
    }));
  };

  const deleteAllCoordinates = () => {
    const isPublic = template !== undefined;
    deleteCoordinatesFromStore(project.id, isPublic);
    setGPSSource(undefined);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      gpsSources: [],
      gpsSourceIDs: [],
      styledPoints: [],
      sourceFileNames: [],
      mapLabels: [],
    }));
  };

  const styledPointAtCoordinate = (
    point: Coordinate
  ): { point: StyledPoint; index: number } | undefined => {
    const styledPoints = project.styledPoints ?? [];
    const index = styledPoints.findIndex((p) => {
      return p.coordinate.lat === point.lat && p.coordinate.lng === point.lng;
    });
    if (index === -1) {
      return undefined;
    }
    return { point: styledPoints[index], index };
  };

  const styledPointWithID = (id: string): StyledPoint | undefined => {
    const styledPoints = project.styledPoints ?? [];
    return styledPoints.find((p) => p.id === id);
  };

  const setStyledPoint = (point: StyledPoint) => {
    setPointStyle(point.coordinate, point.style, point.id, point.label);
  };

  const createStyledPointFromPhoto = async (
    photo: PhotoWithCoordinates
  ): Promise<StyledPoint> => {
    const url = photo.url;
    const coordinates = photo.coordinates;
    //get the last path component of the URL
    const filenameWithExtension = url.split("/").pop()?.split("?")[0] || "";
    const filename = filenameWithExtension.replace(/\.[^/.]+$/, "");

    const dest = "projects/" + project.id + "/labels";
    await duplicateUserImage("uploads", filename, "jpeg", dest, filename);

    const pointID = uuid();

    const mapLabel: MapLabel = {
      id: uuid(),
      pointID: pointID,
      coordinate: coordinates,
      text: "",
      type: "image",
      style: {
        ...project.mapLabelStyle,
        imageName: filename,
        showImage: true,
      },
      editingPermissions: project.editingPermissions.mapLabel,
    };

    cacheImageForMapLabelStyle(mapLabel.style);

    const point: StyledPoint = {
      id: pointID,
      coordinate: coordinates,
      style: project.pointStyle,
      editingPermissions: project.editingPermissions.point,
      label: mapLabel,
    };
    setStyledPoint(point);

    setStyledPointsRequiringBoundsUpdate((prev) => {
      if (prev.includes(point)) {
        return prev; // No need to add it again.
      }
      return [...prev, point];
    });

    return point;
  };

  const clearStyledPointsRequiringBoundsUpdate = () => {
    setStyledPointsRequiringBoundsUpdate([]);
  };

  const styledPointExistsForPhoto = (url: string) => {
    const filenameWithExtension = url.split("/").pop()?.split("?")[0] || "";
    const filename = filenameWithExtension.replace(/\.[^/.]+$/, "");

    const styledPoints = project.styledPoints ?? [];
    return styledPoints.some((point) => {
      return point.label?.style.imageName === filename;
    });
  };

  const removeStyledPointForPhoto = (url: string) => {
    const filenameWithExtension = url.split("/").pop()?.split("?")[0] || "";
    const filename = filenameWithExtension.replace(/\.[^/.]+$/, "");

    const styledPoints = project.styledPoints ?? [];
    let point = styledPoints.filter((point) => {
      return point.label?.style.imageName === filename;
    })[0];
    let coordinate = point.coordinate;
    if (coordinate) {
      deleteCoordinate(coordinate);
    }
    // removes;
  };

  const setPointStyle = (
    point: Coordinate,
    style: PointStyle | undefined,
    existingID: string | undefined,
    label: MapLabel | undefined
  ) => {
    const styledPoints = project.styledPoints ?? [];
    const existing = styledPoints.findIndex((p) => {
      return p.coordinate.lat === point.lat && p.coordinate.lng === point.lng;
    });

    if (existing >= 0) {
      if (!style) {
        styledPoints.splice(existing, 1);
      } else {
        styledPoints[existing].style = style;
        styledPoints[existing].label = label;
      }
    } else if (style) {
      styledPoints.push({
        id: existingID ?? uuid(),
        coordinate: point,
        style,
        editingPermissions: project.editingPermissions.point,
        label,
      });
    }

    //Default label style for next time.
    const mapLabelStyle: MapLabelStyle = {
      ...(label?.style ?? project.mapLabelStyle),
      imageName: undefined,
    };

    let lastUsedColors:
      | { mainColor: string; secondaryColor: string }
      | undefined;
    if (style) {
      lastUsedColors = {
        mainColor: style.mainColor,
        secondaryColor: style.secondaryColor ?? "white",
      };
    }

    const isPhotoLabel = label?.style.imageName !== undefined;
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      pointStyle: style ?? prevProject.pointStyle,
      styleUpdateKey: Date.now().toString(),
      styledPoints,
      lastUsedStylePointColors: lastUsedColors,
      mapLabelStyle: mapLabelStyle,
      //This was the previous way of adjusting bounds for newly added points, but of course
      //it reset the bounds whenever a point was adjusted, causing the map to re-center
      //unnecessarily.  The new approach is to :
      // 1. When a label is added, add it's cache to a "labelsRequiringBoundsUpdate" array on this provider.
      // 2. When the map is rendered, check if any IDs exist in that array, and if so, adjust the bounds to
      // include them.
      // 3. Clear that array.
      // bounds: isPhotoLabel ? undefined : prevProject.bounds,
    }));
  };

  const addNewStyledPoint = (coordinate: Coordinate) => {
    const pointID = uuid();
    const label = addNewMapLabel(coordinate, "text", pointID);

    const point: StyledPoint = {
      id: pointID,
      coordinate: coordinate,
      style: { ...project.pointStyle, imageName: undefined },
      editingPermissions: project.editingPermissions.point,
      label: label,
    };
    setPointStyle(coordinate, point.style, point.id, label);
    return point;
  };

  const removeAllStyledPoints = () => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints: [],
    }));
  };

  const setStyledPointEditingPermissions = (
    point: StyledPoint,
    permissions: PointEditingPermissions
  ) => {
    const styledPoints = project.styledPoints ?? [];
    const existing = styledPoints.findIndex((p) => {
      return (
        p.coordinate.lat === point.coordinate.lat &&
        p.coordinate.lng === point.coordinate.lng
      );
    });
    if (existing >= 0) {
      styledPoints[existing].editingPermissions = permissions;
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints,
    }));
  };

  // const getMapLabel = (id: string): MapLabel | undefined => {
  //   let labels = project.mapLabels ?? [];
  //   return labels.find((p) => {
  //     return p.id === id;
  //   });
  // };

  const setMapLabel = (label: MapLabel) => {
    // There should always be a map label.
    // This function is not for creating them - they are created when a
    // styled point is created.
    let point = styledPointWithID(label.pointID);
    if (!point) {
      console.warn("No styled point found for label with ID: " + label.id);
      return;
    }
    const updated: StyledPoint = {
      ...point,
      label: label,
    };
    setStyledPoint(updated);
  };

  const addNewMapLabel = (
    coordinate: Coordinate,
    type: MapLabelType,
    pointID: string
  ) => {
    const id = uuid();
    let style = project.mapLabelStyle;
    if (style.showImage === undefined && style.showText === undefined) {
      style = { ...style, showText: true };
    }
    const label: MapLabel = {
      id,
      pointID,
      coordinate: coordinate,
      text: "Label",
      type: type,
      style: { ...style },
      editingPermissions: project.editingPermissions.mapLabel,
    };
    //We're going to add map labels nested under a StyledPoint.
    //setMapLabel(label);
    return label;
  };

  // const removeMapLabel = (label: MapLabel) => {
  //   const labels = project.mapLabels.filter((l) => l.id !== label.id);
  //   setProject((prevProject) => ({
  //     ...prevProject,
  //     isRendered: false,
  //     mapLabels: labels,
  //     labelUpdateKey: Date.now().toString(),
  //   }));
  // };

  const getStaticLabel = (id: string): StaticLabel | undefined => {
    let labels = project.staticLabels ?? [];
    return labels.find((p) => {
      return p.id === id;
    });
  };

  const setStaticLabel = (label: StaticLabel) => {
    let labels = project.staticLabels ?? [];
    const existing = labels.findIndex((p) => {
      return p.id === label.id;
    });
    // Mark label as edited when updating (not when first created with empty text)
    const updatedLabel = label.text ? { ...label, isEdited: true } : label;
    if (existing >= 0) {
      labels[existing] = updatedLabel;
    } else {
      labels.push(updatedLabel);
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      staticLabels: labels,
      labelUpdateKey: Date.now().toString(),
      staticLabelStyle: label.style,
    }));
  };

  const addNewStaticLabel = (position: Point, relativeFontSize: number) => {
    const id = uuid();
    const label: StaticLabel = {
      id,
      position,
      text: "",
      description: "Label",
      style: { ...project.staticLabelStyle, fontSize: relativeFontSize },
      editingPermissions: project.editingPermissions.staticLabel,
    };
    setStaticLabel(label);
    return label;
  };

  const removeStaticLabel = (label: StaticLabel) => {
    const labels = project.staticLabels.filter((l) => l.id !== label.id);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      staticLabels: labels,
      labelUpdateKey: Date.now().toString(),
    }));
  };

  const getBackdrop = (id: string) => {
    return project.backdrops.find((backdrop) => backdrop.id === id);
  };

  const setBackdrop = (backdrop: Backdrop) => {
    let backdrops = project.backdrops ?? [];
    const existing = backdrops.findIndex((b) => b.id === backdrop.id);
    if (existing >= 0) {
      backdrops[existing] = backdrop;
    } else {
      backdrops.push(backdrop);
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      backdrops,
      backdropUpdateKey: Date.now().toString(),
      backdropStyle: backdrop.style,
    }));
  };

  const setBackdrops = (backdrops: Backdrop[]) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      backdrops,
    }));
  };

  const addNewBackdrop = (point: Point, type: BackdropType) => {
    const id = uuid();
    const count = project.backdrops.length;
    const backdrop: Backdrop = {
      id,
      position: point,
      type,
      size: { width: 0.3, height: 0.5 },
      style: { ...project.backdropStyle },
      name: `Backdrop ${count + 1}`,
      editingPermissions: project.editingPermissions.backdrop,
    };
    setBackdrop(backdrop);
    return backdrop;
  };

  const removeBackdrop = (backdrop: Backdrop) => {
    const backdrops = project.backdrops.filter((b) => b.id !== backdrop.id);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      backdrops,
    }));
  };

  const staticMapImageURL = (options: MapStaticImageOptions) => {
    return getStaticMapURL(options);
  };

  const staticQuadrantImageURL = (options: MapStaticImageOptions) => {
    return getQuadrantURLs(options);
  };

  const setCoordinates = (
    coordinates: Coordinate[],
    sourceFileName: string,
    data: GPSData | undefined
  ) => {
    const gpsSourceID = setReduceCoordinates(coordinates, sourceFileName, data);

    setImmediateGPSSourceSaveSemaphore(true);
    setProject((prevProject) => ({
      ...prevProject,
      bounds: undefined,
      isRendered: false,
      gpsSourceIDs: [gpsSourceID],
      gpsData: data ? { id: gpsSourceID, data: data } : undefined,
      center: undefined,
      zoom: undefined,
    }));
  };

  const setMapStyle = (styleID: string) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      mapStyle: MapStyleBaseURL + styleID,
    }));
  };

  const setMainRouteColor = (color: string) => {
    const style: LineStyle = { ...project.lineStyle, mainColor: color };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setFlankRouteColor = (color: string) => {
    const style: LineStyle = { ...project.lineStyle, flankColor: color };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLinePattern = (pattern: LinePattern) => {
    let style: LineStyle = { ...project.lineStyle, mainPattern: pattern };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLineDotDash = (dotDash: boolean) => {
    let style: LineStyle = { ...project.lineStyle, dotDash: dotDash };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLineShadow = (shadow: Shadow | undefined) => {
    let style: LineStyle = { ...project.lineStyle, shadow };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLineWidth = (width: number) => {
    let style = { ...project.lineStyle, width };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setCoordinateBounds = (bounds: CoordinateBounds | undefined) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      bounds,
    }));
  };

  const applyBorderAndPointerStyleToAllPoints = (style: MapLabelStyle) => {
    const newPoints = applyLabelStyleToAllPointsInProject(
      style,
      "pointerAndBorder",
      project
    );
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints: newPoints,
      mapLabelStyle: style,
      styleUpdateKey: Date.now().toString(),
    }));
  };

  const setBorderWidthMM = (width: number) => {
    const borderStyle = { ...project.borderStyle, borderWidth: width };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      borderStyle,
    }));
  };
  const setBorderColor = (color: string) => {
    const borderStyle = { ...project.borderStyle, borderColor: color };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      borderStyle,
    }));
  };

  const updateTemplate = (template: ProjectTemplate) => {
    setTemplate({ ...template });
  };

  const setTemplateThumbnail = (thumbnail: string) => {
    setTemplate((prevTemplate) => {
      if (!prevTemplate) {
        return undefined;
      }
      return { ...prevTemplate, thumbnail };
    });
  };

  const setEditingPermissions = (editingPermissions: EditingPermissions) => {
    setProject((prevProject) => ({
      ...prevProject,
      editingPermissions,
    }));
  };

  const mapStyleID = project.mapStyle.replace(MapStyleBaseURL, "");

  const contextValue = useMemo(
    () => ({
      mapStyleID,
      project,
      setLastRenderID,
      markRendered,
      template,
      loadSignedURLS,
      cacheImageForPointStyle,
      cacheImageForMapLabelStyle,
      cacheImageForBackdropStyle,
      setTemplate: updateTemplate,
      isTemplateEditing,
      setEditingPermissions,
      setTemplateThumbnail,
      saveDraft,
      existingEditedDraftID,
      deleteDraft,
      resetProject,
      selectionColor,
      setCoordinateBounds,
      setMapStyle,
      mapStyles: mapStyleThumbnails,
      setMainRouteColor,
      setFlankRouteColor,
      setLineWidth,
      setLinePattern,
      setLineDotDash,
      setLineShadow,
      setCoordinateLimit,
      setCoordinates,
      gpsSource,
      posterSizePresets: sizePresets,
      setSizePresetID,
      setOrientation,
      setMapSize,
      setBorderColor,
      setBorderWidthMM,
      applyBorderAndPointerStyleToAllPoints,
      deleteCoordinate,
      deleteAllCoordinates,
      styledPointAtCoordinate,
      setStyledPoint,
      setPointStyle,
      addNewStyledPoint,
      createStyledPointFromPhoto,
      styledPointExistsForPhoto,
      removeStyledPointForPhoto,
      removeAllStyledPoints,
      setStyledPointEditingPermissions,
      styledPointWithID,
      // getMapLabel,
      setMapLabel,
      addNewMapLabel,
      styledPointsRequiringBoundsUpdate,
      clearStyledPointsRequiringBoundsUpdate,
      // removeMapLabel,
      getStaticLabel,
      setStaticLabel,
      addNewStaticLabel,
      removeStaticLabel,
      getBackdrop,
      setBackdrop,
      setBackdrops,
      addNewBackdrop,
      removeBackdrop,
      staticMapImageURL: staticMapImageURL,
      staticQuadrantImageURL,
      imageCache,
    }),
    [
      mapStyleID,
      project,
      template,
      isTemplateEditing,
      gpsSource,
      sizePresets,
      mapStyleThumbnails,
      styledPointsRequiringBoundsUpdate,
      imageCache,
      selectionColor,
      setLastRenderID,
      markRendered,
      setSizePresetID,
      setOrientation,
    ]
  );

  return (
    <PosterProjectContext.Provider value={contextValue}>
      {children}
    </PosterProjectContext.Provider>
  );
};

function usePosterProject() {
  const context = useContext(PosterProjectContext);
  if (!context) {
    throw new Error("usePosterProject must be used within an ProjectProvider");
  }
  return context;
}
export { PosterProjectProvider, usePosterProject };
